import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container } from "@material-ui/core";
import PageTitle from "../../components/pageTitle/PageTitle";
import AuthContext from "../../hooks/authContext";
import { getCompanyClients } from "../../services/apiservices/client";
import { Activity, BarChart3, Building2, Camera, ChevronLeft, ChevronRight, Clock, Download, Eye, Filter, Loader2, Mail, MapPin, Phone, PieChart, Send, Users, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/Tabs/Tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useMediaQuery } from "@mui/material";
import OverviewHeader from "./ProofOfWork/Overview";
import { getCompanyEmployees } from "../../services/apiservices/employee";
import moment from "moment";
import EmployeeTab from "./ProofOfWork/EmployeeTab";
import { subscribeToAppointmentsByDates, downloadImage } from "../../services/apiservices/appoinments";
import SitesTabContent from "./ProofOfWork/Sites";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { RadioGroup, RadioGroupItem } from '../../components/ui/radioGroup';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Tooltip, TooltipProvider } from "../../components/ui/tooltip";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { cn } from "../../components/ui/utils";
import { sendProofOfServiceReminder } from "../../services/apiservices/notifications";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "../../components/ui/sheet";

const ProofOfService = () => {
    const auth = useContext(AuthContext);
    const [clients, setClients] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [clientNameList, setClientNameList] = useState([]);
    const [rawAppointments, setRawAppointments] = useState([]);
    const [dateRange, setDateRange] = useState('today');
    const [selectedSite, setSelectedSite] = useState('All');
    const [activeTab, setActiveTab] = useState('overview');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showAdvancedView, setShowAdvancedView] = useState(false);
    const [messageRecipients, setMessageRecipients] = useState([]);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeHistoryTimeFrame, setEmployeeHistoryTimeFrame] = useState('last7');
    const [messageType, setMessageType] = useState('both');
    const [showEmployeeDetailModal, setShowEmployeeDetailModal] = useState(false);
    const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [currentShiftPhotos, setCurrentShiftPhotos] = useState([]);
    const [tempDateRange, setTempDateRange] = useState(dateRange);
    const [tempSelectedSites, setTempSelectedSites] = useState([selectedSite]);

    const isMobile = useMediaQuery("(max-width:767px)");
    const localClientID = localStorage.getItem("clientId");
    const localCompanyId = localStorage.getItem("companyId");
    const companyId = auth?.userProfile?.role === "Client" ? localCompanyId ? localCompanyId : auth?.userProfile?.companyId : auth?.userProfile?.role === "companyUser" ? auth?.userProfile?.id : auth?.userProfile?.companyId;

    const tabOptions = {
        overview: { label: 'Overview', icon: PieChart },
        employees: { label: 'Employees', icon: Users },
        sites: { label: 'Sites', icon: Building2 },
        analytics: { label: 'Analytics', icon: BarChart3 },
    };

    const DateOptions = [
        {label: "Today", value: "today"},
        {label: "Yesterday", value: "yesterday"},
        {label: "Last 7 Days", value: "last7"},
        {label: "Last 30 Days", value: "last30"}
    ];

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (dateRange !== 'today') count++;
        if (selectedSite !== 'All') count++;
        return count;
    }, [dateRange, selectedSite]);

    const siteFilteredAppointments = useMemo(() => {
      if (selectedSite === 'All') return rawAppointments;
      return rawAppointments?.filter(appt => appt.clientId === selectedSite) || [];
    }, [rawAppointments, selectedSite]);

      useEffect(() => {    
        const startDate = moment().subtract(30, "days").startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const endDate = moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss");
    
        const clientId = auth?.userProfile?.role === "Client" ? auth?.userProfile?.client : null;
        const unsubscribe = subscribeToAppointmentsByDates(
          startDate,
          endDate,
          companyId,
          clientId,
          (appointments) => {
            if (appointments?.length > 0) {
              setRawAppointments(appointments || []);
            } else {
               setRawAppointments([]);
            }
          },
          (error) => {
            console.log(error);
            setRawAppointments([]);
          }
        );
    
        return () => unsubscribe && unsubscribe();
      }, [companyId]);

     useEffect(() => {
        const getClientsData = async () => {
          const [clientRes, employeeRes] = await Promise.all([
            new Promise((resolve) => getCompanyClients(companyId, resolve)),
            new Promise((resolve, reject) => { getCompanyEmployees(companyId, (res) => resolve(res), (err) => resolve({ status: false, data: [] }));}),
          ]);
    
          const details = Array.isArray(clientRes?.data) ? clientRes?.data : [];
    
          if (auth?.userProfile?.role === "Client") {
            const clientId = auth?.userProfile?.client;
    
            const client = details?.find((item) => item.id === clientId);
            if (client) {
              const clientOption = {
                value: client?.id,
                label: client?.firstName,
              };
    
              setClients([client]);
              setClientNameList([clientOption]);
            }
            return;
          }
    
          const clientOptions = details?.map((item) => ({
            value: item.id,
            label: item.firstName,
          }))?.sort((a, b) => a?.label?.localeCompare(b?.label))

          const employeeDetails = Array.isArray(employeeRes?.data) ? employeeRes?.data?.filter((item)=> item?.status === true) : [];
    
          setClients(details);
          setClientNameList([{ label: "All Sites", value: "All" }, ...clientOptions]);
          setEmployees(employeeDetails);
        };
    
        if (companyId) {
          getClientsData();
        }
      }, [companyId]);

    const handleSendReminder = (employeeIds) => {
        setMessageRecipients(employeeIds);
        setShowMessageModal(true);
    };

    const openPhotoGallery = (photos, startIndex = 0) => {
        setCurrentShiftPhotos(photos);
        setCurrentPhotoIndex(startIndex);
        setPhotoGalleryOpen(true);
    };

    const prevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev === 0 ? currentShiftPhotos.length - 1 : prev - 1));
    };

    const nextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev === currentShiftPhotos.length - 1 ? 0 : prev + 1));
    };

    const handleDownload = async () => {
        const photo = currentShiftPhotos[currentPhotoIndex];
        const photoUrl = typeof photo === 'string' ? photo : (photo?.url || photo?.proofUrl);
        if (!photoUrl) return;

        try {
            const blob = await downloadImage(photoUrl);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `proof-${moment().format("YYYYMMDD-HHmmss")}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        }
    };

    const handleSendMessage = async () => {
        setIsSending(true);
        const recipients = employees.filter(emp => messageRecipients.includes(emp.id));
        
        await sendProofOfServiceReminder(recipients, messageType, (res) => {
             setIsSending(false);
             setShowMessageModal(false);
             setMessageRecipients([]);
             setSelectedEmployeeIds([]);
        }, (err) => {
             setIsSending(false);
        });
    };

    const handleApplyMobileFilters = () => {
      setDateRange(tempDateRange);
      setSelectedSite(tempSelectedSites[0] || 'All');
      setShowMobileFilters(false);
    };

    const handleClearMobileFilters = () => {
      setTempDateRange('today');
      setSelectedSite('All');
      setTempSelectedSites(['All']);
    };

    const selectedEmployeeStats = useMemo(() => {
        if (!selectedEmployee || !siteFilteredAppointments) return null;

        const now = moment();
        let rangeStartDate, rangeEndDate;

        switch (employeeHistoryTimeFrame) {
            case "today":
                rangeStartDate = now.format("YYYY-MM-DD");
                rangeEndDate = now.format("YYYY-MM-DD");
                break;
            case "yesterday":
                const yesterday = moment().subtract(1, "day");
                rangeStartDate = yesterday.format("YYYY-MM-DD");
                rangeEndDate = yesterday.format("YYYY-MM-DD");
                break;
            case "last7":
                rangeStartDate = moment().subtract(7, "days").format("YYYY-MM-DD");
                rangeEndDate = now.format("YYYY-MM-DD");
                break;
            case "last30":
            default:
                rangeStartDate = moment().subtract(30, "days").format("YYYY-MM-DD");
                rangeEndDate = now.format("YYYY-MM-DD");
                break;
        }

        const empAppointments = siteFilteredAppointments.filter(appt => 
            appt.userId === selectedEmployee.id &&
            moment(appt.appointmentDateTime).format("YYYY-MM-DD") >= rangeStartDate &&
            moment(appt.appointmentDateTime).format("YYYY-MM-DD") <= rangeEndDate
        );

        let missing = 0;
        let late = 0;
        let invalid = 0;
        let onTime = 0;
        let total = 0;
        const detailedShifts = [];
        const dailyStats = {};

        const clientMap = clients?.reduce((acc, c) => {
            acc[c.id] = c?.clientName || c?.firstName;
            return acc;
        }, {}) || {};

        let latestShift = null;

        empAppointments.forEach(appt => {
             const details = appt?.apoointmentDetails || {};
             const shiftDay = moment(appt.appointmentDateTime);

             if (!latestShift || shiftDay.isAfter(latestShift.date)) {
                 latestShift = { date: shiftDay, site: clientMap[appt.clientId] || "Unknown" };
             }

             const dateStr = shiftDay.format("YYYY-MM-DD");

             let shiftEnd = null;
             if (details.endTime && appt.appointmentDateTime) {
                 const baseDate = moment(appt.appointmentDateTime);
                 const timeStr = details.endTime; 
                 const dStr = baseDate.format("YYYY-MM-DD");
                 shiftEnd = moment(`${dStr} ${timeStr}`, "YYYY-MM-DD hh:mm a");
                 if (shiftEnd.isValid() && shiftEnd.isBefore(baseDate)) {
                     shiftEnd.add(1, 'day');
                 }
             } else if (appt.appointmentDateTime) {
                 shiftEnd = moment(appt.appointmentDateTime).add(details.duration || 180, 'minutes');
             }

             let requiredCount = 0;
             let hasMissing = false;
             let hasInvalid = false;
             let hasLate = false;
             let allVerified = true;
             const collectedPhotos = [];

             Object.values(appt?.tasks || {})?.forEach((group) => {
                (group?.tasksList || [])?.forEach((task) => {
                    if (task.pos === true) {
                        requiredCount++;
                        const proofs = task?.proofs || [];
                        
                        if (proofs.length > 0) {
                             const validProofs = proofs.filter(p => p.verified === true && p.source === 'camera');
                             if (validProofs.length === 0) {
                                 hasInvalid = true;
                             } else if (shiftEnd) {
                                 const isLate = validProofs.some((proof) => {
                                    const ts = proof?.captured_at_device;
                                    if (!ts) return false;
                                    const proofTime = typeof ts === "object" && ts.seconds ? moment.unix(ts.seconds) : moment(ts);
                                    return proofTime.isValid() && proofTime.isAfter(shiftEnd);
                                 });
                                 if (isLate) hasLate = true;
                             }

                             proofs.forEach(proof => {
                                 if (proof.url) collectedPhotos.push({ url: proof.url, verified: proof.verified === true, gps: proof.gps, capturedAt: proof.captured_at_device });
                             });

                             if (!proofs.every(p => p.verified === true)) allVerified = false;
                        } else {
                            hasMissing = true;
                            allVerified = false;
                        }
                    }
                });
             });

             if (requiredCount === 0) return;
             total++;
             
             let shiftStatus = "complete";
             let statusText = "On Time";
             if (hasMissing) { missing++; shiftStatus = "missing"; statusText = "Missing Proof"; }
             else if (hasInvalid) { invalid++; shiftStatus = "missing"; statusText = "Not Verified"; }
             else if (hasLate) { late++; shiftStatus = "late"; statusText = "Late Proof"; }
             else { onTime++; statusText = allVerified ? "Verified" : "Pending Verification"; }

             detailedShifts.push({ date: shiftDay.format("MMM DD, YYYY"), shiftName: details?.shiftName || "Shift", site: clientMap[appt.clientId] || "Unknown", startTime: details?.time || "-", endTime: details?.endTime || "-", status: shiftStatus, duration: details?.duration || "-", statusText, photoCount: collectedPhotos.length, photos: collectedPhotos, location: hasMissing ? "Missing Proof" : hasInvalid ? "Not Verified" : allVerified ? "Verified via geofencing" : "Verification Pending", appointmentDate: shiftDay.format("MMM DD, YYYY") });

             if (!dailyStats[dateStr]) dailyStats[dateStr] = { total: 0, onTime: 0 };
             dailyStats[dateStr].total++;
             if (shiftStatus === "complete") dailyStats[dateStr].onTime++;
        });

        const completionRate = total > 0 ? Math.round((onTime / total) * 100) : 0;
        const trend = Object.keys(dailyStats).sort().map(date => dailyStats[date].total > 0 ? Math.round((dailyStats[date].onTime / dailyStats[date].total) * 100) : 0);

        return { ...selectedEmployee, site: latestShift?.site || selectedEmployee.site, completionRate, missingProofs: missing, lateProofs: late, invalidProofs: invalid, completed: onTime, totalShifts: total, detailedShifts: detailedShifts.sort((a, b) => moment(b.date, "MMM DD, YYYY").valueOf() - moment(a.date, "MMM DD, YYYY").valueOf()), trend };
    }, [selectedEmployee, siteFilteredAppointments, employeeHistoryTimeFrame, clients]);

    return (
      <Container>
        <PageTitle
          pageTitle="Proof of Service"
          showBreadcrumb={[{ label: "Home", to: "/" }, { label: "Operations" }]}
          BreadCrumbCurrentPageTitle={"Proof of Service"}
          showLastSeen={true}
        />

        <div className="px-0 sm:px-4 md:px-6 py-4 sm:py-6 space-y-6 animate-in fade-in duration-500">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center px-3 sm:px-0 mb-30">
            {isMobile ? (
              <div className="space-y-3 w-full">
                <Button
                  variant="outline"
                  onClick={() => setShowMobileFilters(true)}
                  className="w-full h-12 justify-start transition-all hover:bg-muted"
                >
                  <Filter className="h-4 w-4 mr-8" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-8 animate-in zoom-in"
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant={showAdvancedView ? "default" : "outline"}
                  onClick={() => setShowAdvancedView(!showAdvancedView)}
                  className="w-full h-12 font-medium transition-all"
                >
                  {showAdvancedView ? "Simple View" : "Advanced View"}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end px-3 sm:px-0 w-full">
                <div className="w-[250px]">
                  <SingleSelectNew
                    contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                    itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                    value={dateRange}
                    options={DateOptions}
                    name="dateRange"
                    label="Date Range"
                    onChange={(e) => setDateRange(e)}
                  />
                </div>

                <div className="w-[250px]">
                  <SingleSelectNew
                    contentClass="bg-white text-black border-gray-300 dark:bg-[#2c3440] dark:text-white dark:border-[#464c59]"
                    itemClass="hover:bg-gray-100 dark:hover:bg-[#3c4351]"
                    value={selectedSite}
                    options={clientNameList}
                    name="sites"
                    label="All Sites"
                    onChange={(e) => setSelectedSite(e)}
                  />
                </div>

                <Button
                  variant={showAdvancedView ? "default" : "outline"}
                  onClick={() => setShowAdvancedView(!showAdvancedView)}
                  className="w-[140px] h-[42px] font-medium transition-all ml-auto"
                >
                  {showAdvancedView ? "Simple View" : "Advanced View"}
                </Button>
              </div>
            )}
          </div>

          <Tabs
            key={activeTab}
            defaultValue={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6 px-3 sm:px-0"
          >
            {isMobile ? (
              <div className="w-full">
                <Label htmlFor="tab-select" className="sr-only">
                  Navigate between sections
                </Label>
                <Select value={activeTab} onValueChange={setActiveTab}>
                  <SelectTrigger
                    id="tab-select"
                    className="w-full h-12 text-base font-medium"
                  >
                    <div className="flex items-center gap-2">
                      {tabOptions[activeTab] && (
                        <>
                          {React.createElement(tabOptions[activeTab].icon, { className: "h-4 w-4" })}
                          <span>{tabOptions[activeTab].label}</span>
                        </>
                      )}
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(tabOptions).map(([key, { label, icon: Icon }]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          <span>{label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <TabsList className="grid w-full grid-cols-4 h-auto p-4 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] bg-[#f1f5f9ff]">
                  <TabsTrigger
                    value="overview"
                    aria-label="Overview tab - View compliance summary and key metrics"
                  >
                    <PieChart className="h-4 w-4" />
                    <span className="font-medium">Overview</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="employees"
                    aria-label="Employees tab - Manage employee performance and send reminders"
                  >
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Employees</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="sites"
                    aria-label="Sites tab - Monitor site compliance and trends"
                  >
                    <Building2 className="h-4 w-4" />
                    <span className="font-medium">Sites</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="analytics"
                    aria-label="Analytics tab - Deep dive into performance metrics"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span className="font-medium">Analytics</span>
                  </TabsTrigger>
                </TabsList>
                </>
              )}

                <TabsContent
                  value="overview"
                  className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 px-3 sm:px-0"
                >
                  <OverviewHeader
                    period={dateRange}
                    rawAppointments={siteFilteredAppointments}
                    clients={clients}
                    employees={employees}
                    showAdvancedView={showAdvancedView}
                    setShowMessageModal={setShowMessageModal}
                    setMessageRecipients={setMessageRecipients}
                    setSelectedEmployee={setSelectedEmployee}
                    setShowEmployeeDetailModal={setShowEmployeeDetailModal}
                    setEmployeeHistoryTimeFrame={setEmployeeHistoryTimeFrame}
                  />
                </TabsContent>

                <TabsContent
                  value="employees"
                  className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 px-3 sm:px-0"
                >
                  <EmployeeTab
                    employees={employees}
                    rawAppointments={siteFilteredAppointments}
                    clients={clients}
                    dateRange={dateRange}
                    setShowMessageModal={setShowMessageModal}
                    setMessageRecipients={setMessageRecipients}
                    setSelectedEmployeeIds={setSelectedEmployeeIds}
                    selectedEmployeeIds={selectedEmployeeIds}
                    setShowEmployeeDetailModal={setShowEmployeeDetailModal}
                    setEmployeeHistoryTimeFrame={setEmployeeHistoryTimeFrame}
                    setSelectedEmployee={setSelectedEmployee}
                  />
                </TabsContent>

                <TabsContent
                  value="sites"
                  className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 px-3 sm:px-0"
                >
                  <SitesTabContent
                    clients={clients}
                    setActiveTab={setActiveTab}
                    rawAppointments={siteFilteredAppointments}
                    dateRange={dateRange}
                  />
                </TabsContent>

                <TabsContent
                  value="analytics"
                  className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 px-3 sm:px-0"
                >
                  <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
                    <CardHeader className="p-16">
                      <CardTitle className="text-xl font-bold">
                        Analytics Dashboard
                      </CardTitle>
                      <CardDescription>
                        Deep dive into performance metrics and patterns
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-16">
                      <p className="text-muted-foreground text-center py-12">
                        Advanced analytics coming soon. This section will
                        include predictive insights, pattern analysis, and
                        custom reporting.
                      </p>
                    </CardContent>
                  </Card>
                </TabsContent>
          </Tabs>
        </div>

        <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
          <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl">
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription>Refine your view with these filters</SheetDescription>
            </SheetHeader>
            <div className="space-y-6 py-6">
              <div className="space-y-2">
                <Label className="text-base font-semibold dark:text-white">Date Range</Label>
                <RadioGroup value={tempDateRange} onValueChange={(value) => setTempDateRange(value)}>
                  <div className="flex items-center space-x-2 p-12 rounded-md hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="today" id="mobile-today" />
                    <Label htmlFor="mobile-today" className="font-normal cursor-pointer flex-1 dark:text-white">Today</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-12 rounded-md hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="yesterday" id="mobile-yesterday" />
                    <Label htmlFor="mobile-yesterday" className="font-normal cursor-pointer flex-1 dark:text-white">Yesterday</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-12 rounded-md hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="last7" id="mobile-last7" />
                    <Label htmlFor="mobile-last7" className="font-normal cursor-pointer flex-1 dark:text-white">Last 7 Days</Label>
                  </div>
                  <div className="flex items-center space-x-2 p-12 rounded-md hover:bg-muted cursor-pointer">
                    <RadioGroupItem value="last30" id="mobile-last30" />
                    <Label htmlFor="mobile-last30" className="font-normal cursor-pointer flex-1 dark:text-white">Last 30 Days</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-semibold dark:text-white">Site</Label>
                <RadioGroup value={tempSelectedSites[0]} onValueChange={(value) => setTempSelectedSites([value])}>
                  {clientNameList?.map((client) => (
                    <div key={client?.value} className="flex items-center space-x-2 p-12 rounded-md hover:bg-muted cursor-pointer">
                      <RadioGroupItem value={client?.value} id={`mobile-site-${client?.value}`} />
                      <Label htmlFor={`mobile-site-${client?.value}`} className="font-normal cursor-pointer flex-1 dark:text-white">{client?.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
              </div>

            <SheetFooter className="gap-2">
              <Button variant="outline" onClick={handleClearMobileFilters} className="flex-1">
                Clear All
              </Button>
              <Button onClick={handleApplyMobileFilters} className="flex-1">
                Apply Filters
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        <Dialog
          open={showEmployeeDetailModal}
          onOpenChange={setShowEmployeeDetailModal}
        >
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              {selectedEmployeeStats && (
                <div className="flex items-start gap-4">
                  <div className="h-20 w-[80px] rounded-full bg-muted border-4 border-background shadow-lg flex items-center justify-center shrink-0">
                    <Users className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <DialogTitle className="text-2xl dark:text-white">
                      {selectedEmployeeStats?.name}
                    </DialogTitle>
                    <DialogDescription className="mt-4 space-y-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        <span>{selectedEmployeeStats?.site}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{selectedEmployee?.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <span>{selectedEmployee?.phone}</span>
                      </div>
                    </DialogDescription>
                    <div className="mt-12">
                      <p className="text-sm text-muted-foreground">
                        Completion Rate:{" "}
                        <span className="font-bold text-lg text-foreground">
                          {selectedEmployeeStats?.completionRate}%
                        </span>{" "}
                        ({employeeHistoryTimeFrame === 'today' ? 'Today' : employeeHistoryTimeFrame === 'yesterday' ? 'Yesterday' : employeeHistoryTimeFrame === 'last7' ? 'Last 7 Days' : 'Last 30 Days'})
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </DialogHeader>

            {selectedEmployeeStats && (
              <div className="space-y-6 mt-24">
                <div>
                  <Label className="text-sm font-semibold mb-12 block dark:text-white">
                    View History For:
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {["today", "yesterday", "last7", "last30"]?.map((range) => (
                      <Button
                        key={range}
                        variant={
                          employeeHistoryTimeFrame === range
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => setEmployeeHistoryTimeFrame(range)}
                        className="transition-all"
                      >
                        {range === "today"
                          ? "Today"
                          : range === "yesterday"
                            ? "Yesterday"
                            : range === "last7"
                              ? "Last 7 Days"
                              : "Last 30 Days"}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Card className="border-l-4 border-l-red-500">
                    <CardContent className="p-16">
                      <p className="text-xs text-muted-foreground mb-4">
                        Missing
                      </p>
                      <p className="text-2xl font-bold text-red-600">
                        {selectedEmployeeStats?.missingProofs}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-orange-500">
                    <CardContent className="p-16">
                      <p className="text-xs text-muted-foreground mb-4">Late</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {selectedEmployeeStats?.lateProofs}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-l-green-500">
                    <CardContent className="p-16">
                      <p className="text-xs text-muted-foreground mb-4">
                        Complete
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {selectedEmployeeStats?.completed}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader className="p-16">
                    <CardTitle className="text-sm font-semibold">
                      Completion Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-16">
                    <ResponsiveContainer
                      width="100%"
                      height={isMobile ? 100 : 120}
                    >
                      <LineChart
                        data={selectedEmployeeStats?.trend?.map((val, idx) => ({
                          day: idx + 1,
                          rate: val,
                        }))}
                      >
                        <Line
                          type="monotone"
                          dataKey="rate"
                          stroke="#10b981"
                          strokeWidth={2}
                          dot={false}
                        />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: isMobile ? 9 : 10 }}
                        />
                        <YAxis
                          domain={[0, 100]}
                          tick={{ fontSize: isMobile ? 9 : 10 }}
                        />
                        <TooltipProvider>
                          <Tooltip />
                        </TooltipProvider>
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <div>
                  <h3 className="text-lg font-bold mb-12 dark:text-white">Recent Shifts</h3>
                  <div className="space-y-3">
                    {selectedEmployeeStats?.detailedShifts?.map((shift, idx) => (
                      <Card
                        key={idx}
                        className={cn(
                          "transition-all hover:shadow-md",
                          shift.status === "complete" &&
                            "border-l-4 border-l-green-500",
                          shift.status === "late" &&
                            "border-l-4 border-l-orange-500",
                          shift.status === "missing" &&
                            "border-l-4 border-l-red-500",
                        )}
                      >
                        <CardContent className="p-16">
                          <div className="flex items-start justify-between gap-3 mb-12">
                            <div className="flex-1">
                              <p className="font-semibold text-sm flex items-center gap-2">
                                {shift?.appointmentDate} • {shift?.shiftName}
                                <Badge
                                  variant={
                                    shift.status === "complete"
                                      ? "default"
                                      : shift.status === "late"
                                        ? "secondary"
                                        : "destructive"
                                  }
                                  className="ml-auto"
                                >
                                  {shift.status === "complete"
                                    ? "✓"
                                    : shift.status === "late"
                                      ? "⚠"
                                      : "✗"}
                                </Badge>
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {shift?.site}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-12 text-xs">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                Start:
                              </span>
                              <span className="font-semibold">
                                {shift?.startTime}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                End:
                              </span>
                              <span className="font-semibold">
                                {shift?.endTime}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Activity className="h-3 w-3 text-muted-foreground" />
                              <span className="font-semibold">
                                {shift?.duration}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs pt-12 border-t">
                            <div className="flex items-center gap-2">
                              <Camera className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {shift?.photoCount > 0
                                  ? `${shift?.photoCount} / ${shift?.photos.length} photos taken`
                                  : "No photos taken"}
                              </span>
                            </div>
                            {shift?.photoCount > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs hover:scale-105 transition-transform"
                                onClick={() => openPhotoGallery(shift?.photos)}
                              >
                                <Eye className="h-3 w-3 mr-4" />
                                View Photos
                              </Button>
                            )}
                          </div>

                          <div className="mt-8 pt-8 border-t">
                            <p
                              className={cn(
                                "text-xs font-medium",
                                shift.status === "complete" && "text-green-600",
                                shift.status === "late" && "text-orange-600",
                                shift.status === "missing" && "text-red-600",
                              )}
                            >
                              Proof Status: {shift?.statusText}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setShowEmployeeDetailModal(false)}
              >
                Close
              </Button>
              {selectedEmployee && (
                <Button
                  onClick={() => handleSendReminder([selectedEmployee?.id])}
                >
                  <Send className="h-4 w-4 mr-8" />
                  Send Reminder
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="dark:text-white">Send Reminder</DialogTitle>
              <DialogDescription>
                Compose a message to send to {messageRecipients.length} employee
                {messageRecipients.length !== 1 ? "s" : ""}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="dark:text-white">Send Via</Label>
                <RadioGroup
                  value={messageType}
                  onValueChange={(value) => setMessageType(value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="msg-email" />
                    <Label
                      htmlFor="msg-email"
                      className="font-normal cursor-pointe dark:text-white"
                    >
                      Email
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="push" id="msg-push" />
                    <Label
                      htmlFor="msg-push"
                      className="font-normal cursor-pointer dark:text-white"
                    >
                      Push Notification
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="both" id="msg-both" />
                    <Label
                      htmlFor="msg-both"
                      className="font-normal cursor-pointer dark:text-white"
                    >
                      Both (Email + Push)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message-preview dark:text-white" className="dark:text-white">
                  Message Preview
                </Label>
                <div className="p-16 border rounded-md bg-muted/50 text-sm space-y-2">
                  <p className="font-semibold dark:text-white">
                    Subject: Proof of Service Reminder
                  </p>
                  <p className="text-muted-foreground dark:text-white">
                    Hi there,
                  </p>
                  <p className="text-muted-foreground dark:text-white">
                    This is a friendly reminder to submit your proof of Service for
                    recent shifts. Please ensure all documentation is uploaded
                    within the required timeframe.
                  </p>
                  <p className="text-muted-foreground mt-8 dark:text-white">
                    If you have any questions, please contact your supervisor.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowMessageModal(false)}
                disabled={isSending}
              >
                Cancel
              </Button>
              <Button onClick={handleSendMessage} disabled={isSending}>
                {isSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-8 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-8" />
                    {messageType === "both"
                      ? "Send Both"
                      : `Send ${messageType === "email" ? "Email" : "Push"}`}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={photoGalleryOpen} onOpenChange={setPhotoGalleryOpen}>
          <DialogContent className="max-w-5xl max-h-[95vh] p-0 bg-black border-none">
            <div className="relative bg-black h-full flex flex-col">
              <div className="flex items-center justify-between p-16 bg-black/80 text-white z-10">
                <div>
                  <p className="font-semibold">Shift Photos</p>
                  <p className="text-sm text-gray-400">Photo {currentPhotoIndex + 1} of {currentShiftPhotos.length}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setPhotoGalleryOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <div className="relative flex-1 flex items-center justify-center bg-black min-h-[60vh]">
                {currentShiftPhotos.length > 0 && (
                  <>
                    <img
                      src={typeof currentShiftPhotos[currentPhotoIndex] === 'string' ? currentShiftPhotos[currentPhotoIndex] : (currentShiftPhotos[currentPhotoIndex]?.url || currentShiftPhotos[currentPhotoIndex]?.proofUrl)}
                      alt={`Shift photo ${currentPhotoIndex + 1}`}
                      className="max-w-full max-h-[70vh] object-contain"
                    />
                    
                    {currentShiftPhotos.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70 hover:scale-110 transition-all"
                          onClick={prevPhoto}
                        >
                          <ChevronLeft className="h-8 w-8" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70 hover:scale-110 transition-all"
                          onClick={nextPhoto}
                        >
                          <ChevronRight className="h-8 w-8" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Thumbnail Strip */}
              {currentShiftPhotos.length > 1 && (
                <div className="p-4 bg-black/80 flex gap-2 overflow-x-auto justify-center">
                  {currentShiftPhotos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={cn(
                        "shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all hover:scale-110",
                        currentPhotoIndex === idx ? "border-white scale-110" : "border-transparent opacity-60"
                      )}
                    >
                      <img src={typeof photo === 'string' ? photo : (photo?.url || photo?.proofUrl)} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <div className="p-4 bg-black/80 text-white border-t border-white/10">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className={cn("h-4 w-4", (typeof currentShiftPhotos[currentPhotoIndex] === 'object' && currentShiftPhotos[currentPhotoIndex]?.verified) ? "text-green-400" : "text-yellow-400")} />
                    <span>
                        Location: {(typeof currentShiftPhotos[currentPhotoIndex] === 'object' && currentShiftPhotos[currentPhotoIndex]?.verified) ? "Verified via geofencing" : "Not Verified"}
                    </span>
                  </div>
                  <Button variant="" size="sm" className="text-white hover:bg-white/20" onClick={handleDownload}>
                    <Download className="h-4 w-4 mr-8" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
        </DialogContent>
      </Dialog>
      </Container>
    );
};

export default ProofOfService;